import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { ClipboardCheck, Plus, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const turnoLabels = {
    manha: 'Manhã (06h-14h)',
    tarde: 'Tarde (14h-22h)',
    noite: 'Noite (22h-06h)'
};

const fadigaLabels = {
    baixo: 'Baixo',
    moderado: 'Moderado',
    alto: 'Alto'
};

const defaultSistemas = [
    'Radar operacional',
    'Rádio VHF funcionando',
    'AIS ativo',
    'Sistema de monitoramento online',
    'Câmeras operacionais',
    'Backup de energia testado'
];

const defaultComunicacao = [
    'Contato com embarcações confirmado',
    'Contato com base confirmado',
    'Telefones de emergência disponíveis',
    'Lista de contatos atualizada'
];

const ChecklistPage = () => {
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const { hasRole, user } = useAuth();

    const [formData, setFormData] = useState({
        turno: 'manha',
        operador_nome: '',
        itens_sistemas: defaultSistemas.map(item => ({ item, verificado: false, observacao: '' })),
        itens_comunicacao: defaultComunicacao.map(item => ({ item, verificado: false, observacao: '' })),
        nivel_fadiga: 'baixo',
        prontidao: true,
        observacoes: ''
    });

    useEffect(() => {
        fetchChecklists();
    }, []);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({ ...prev, operador_nome: user.nome }));
        }
    }, [user]);

    const fetchChecklists = async () => {
        try {
            const response = await axios.get(`${API}/checklists`);
            setChecklists(response.data);
        } catch (error) {
            toast.error('Erro ao carregar checklists');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            await axios.post(`${API}/checklists`, formData);
            toast.success('Checklist registrado!');
            setDialogOpen(false);
            resetForm();
            fetchChecklists();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar checklist');
        }
    };

    const toggleItem = (type, index) => {
        const items = type === 'sistemas' ? [...formData.itens_sistemas] : [...formData.itens_comunicacao];
        items[index].verificado = !items[index].verificado;
        setFormData({
            ...formData,
            [type === 'sistemas' ? 'itens_sistemas' : 'itens_comunicacao']: items
        });
    };

    const resetForm = () => {
        setFormData({
            turno: 'manha',
            operador_nome: user?.nome || '',
            itens_sistemas: defaultSistemas.map(item => ({ item, verificado: false, observacao: '' })),
            itens_comunicacao: defaultComunicacao.map(item => ({ item, verificado: false, observacao: '' })),
            nivel_fadiga: 'baixo',
            prontidao: true,
            observacoes: ''
        });
    };

    const canCreate = hasRole(['admin', 'gestor', 'operador']);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    const getCompletionRate = (checklist) => {
        const total = (checklist.itens_sistemas?.length || 0) + (checklist.itens_comunicacao?.length || 0);
        const checked = (checklist.itens_sistemas?.filter(i => i.verificado).length || 0) + 
                       (checklist.itens_comunicacao?.filter(i => i.verificado).length || 0);
        return total > 0 ? Math.round((checked / total) * 100) : 0;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="checklist-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Checklist Diário
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Verificação de sistemas e prontidão por turno
                    </p>
                </div>
                
                {canCreate && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-checklist"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Novo Checklist
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Checklist de Turno
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Turno
                                        </Label>
                                        <Select 
                                            value={formData.turno} 
                                            onValueChange={(value) => setFormData({...formData, turno: value})}
                                        >
                                            <SelectTrigger data-testid="select-checklist-turno" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(turnoLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nível de Fadiga
                                        </Label>
                                        <Select 
                                            value={formData.nivel_fadiga} 
                                            onValueChange={(value) => setFormData({...formData, nivel_fadiga: value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(fadigaLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Sistemas */}
                                <div>
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono mb-3 block">
                                        Verificação de Sistemas
                                    </Label>
                                    <div className="space-y-2">
                                        {formData.itens_sistemas.map((item, idx) => (
                                            <div 
                                                key={idx}
                                                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-sm"
                                            >
                                                <Checkbox
                                                    checked={item.verificado}
                                                    onCheckedChange={() => toggleItem('sistemas', idx)}
                                                    className="border-slate-600 data-[state=checked]:bg-cyan-600"
                                                />
                                                <span className={`text-sm ${item.verificado ? 'text-white' : 'text-slate-400'}`}>
                                                    {item.item}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Comunicação */}
                                <div>
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono mb-3 block">
                                        Verificação de Comunicação
                                    </Label>
                                    <div className="space-y-2">
                                        {formData.itens_comunicacao.map((item, idx) => (
                                            <div 
                                                key={idx}
                                                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-sm"
                                            >
                                                <Checkbox
                                                    checked={item.verificado}
                                                    onCheckedChange={() => toggleItem('comunicacao', idx)}
                                                    className="border-slate-600 data-[state=checked]:bg-cyan-600"
                                                />
                                                <span className={`text-sm ${item.verificado ? 'text-white' : 'text-slate-400'}`}>
                                                    {item.item}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Prontidão */}
                                <div className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-sm">
                                    <Checkbox
                                        checked={formData.prontidao}
                                        onCheckedChange={(checked) => setFormData({...formData, prontidao: checked})}
                                        className="border-slate-600 data-[state=checked]:bg-emerald-600"
                                    />
                                    <span className="text-white">
                                        Confirmo que estou em plenas condições para executar minhas atividades
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-checklist"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Registrar Checklist
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Checklists Grid */}
            {checklists.length === 0 ? (
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-12 text-center">
                        <ClipboardCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">Nenhum checklist registrado</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {checklists.map((checklist) => {
                        const completionRate = getCompletionRate(checklist);
                        return (
                            <Card 
                                key={checklist.id} 
                                className="bg-slate-900/50 border-slate-800 rounded-sm card-hover"
                            >
                                <CardHeader className="border-b border-slate-800 p-4">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="font-heading text-lg text-white">
                                            {turnoLabels[checklist.turno]?.split(' ')[0]}
                                        </CardTitle>
                                        <span className={`
                                            px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                            ${completionRate === 100 ? 'badge-success' : ''}
                                            ${completionRate >= 50 && completionRate < 100 ? 'badge-warning' : ''}
                                            ${completionRate < 50 ? 'badge-danger' : ''}
                                        `}>
                                            {completionRate}%
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 font-mono">{checklist.data}</p>
                                </CardHeader>
                                <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center gap-2 text-slate-400">
                                        <span className="text-xs">Operador:</span>
                                        <span className="text-sm text-white">{checklist.operador_nome}</span>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                            {checklist.prontidao ? (
                                                <CheckCircle className="w-4 h-4 text-emerald-400" />
                                            ) : (
                                                <XCircle className="w-4 h-4 text-red-400" />
                                            )}
                                            <span className="text-xs text-slate-400">Prontidão</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <AlertTriangle className={`w-4 h-4 ${
                                                checklist.nivel_fadiga === 'baixo' ? 'text-emerald-400' :
                                                checklist.nivel_fadiga === 'moderado' ? 'text-amber-400' : 'text-red-400'
                                            }`} />
                                            <span className="text-xs text-slate-400">
                                                Fadiga: {fadigaLabels[checklist.nivel_fadiga]}
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-xs text-slate-500 pt-2 border-t border-slate-800">
                                        {formatDate(checklist.criado_em)}
                                    </p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ChecklistPage;
